import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useDebouncedEffect from 'common/hooks/useDebouncedEffect';
import * as TranslationsSelectors from 'selectors/TranslationsSelectors';
import * as TranslationsActions from 'actions/TranslationsActions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { TranslationsState } from 'reducers/TranslationsReducer';

/**
 * Hook which handles initialization and saving of translation state
 */

export const useTranslationState = (): TranslationsState['translations'] => {
	const dispatch = useDispatch();
	const shopDefaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const translatables = useSelector(TranslationsSelectors.allTranslatablesById);
	const translatableLanguages = useSelector(TranslationsSelectors.translatableLanguages);
	const activeLanguage = useSelector(TranslationsSelectors.activeLanguage_RAW);
	const committedFields = useSelector(TranslationsSelectors.committedFields);

	const translations = useSelector(TranslationsSelectors.translations);

	/**
	 * Update translation state when translatables change
	 */
	useEffect(() => {
		dispatch(TranslationsActions.setTranslations(translatables));
	}, [translatables, dispatch]);

	/**
	 * Save shop default language to state for easy access
	 */
	useEffect(() => {
		dispatch(TranslationsActions.setDefaultLanguage(shopDefaultLanguage));
	}, [shopDefaultLanguage, dispatch]);

	/**
	 * Set active translation language if not already set
	 */
	useEffect(() => {
		if (!activeLanguage && translatableLanguages.length > 0) {
			dispatch(TranslationsActions.setLanguage(translatableLanguages[0]));
		}
	}, [activeLanguage, translatableLanguages, dispatch]);

	/**
	 * Save pending translations every 1s
	 */
	useDebouncedEffect(
		() => {
			if (committedFields.length > 0) {
				committedFields.forEach((field) => {
					dispatch(TranslationsActions.saveTranslation(field));
				});
			}
		},
		1000,
		[committedFields],
	);

	return translations;
};
